import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { ArrowRight, CheckCircle, Phone, EnvelopeSimple, Clock, CalendarBlank } from "@phosphor-icons/react";
import { format } from "date-fns";
import { ALL_PACKAGES } from "../data";
import { Calendar } from "./ui/calendar";
import { Popover, PopoverTrigger, PopoverContent } from "./ui/popover";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TIME_SLOTS = [
  "8:00 AM", "9:00 AM", "10:00 AM", "11:00 AM",
  "12:00 PM", "1:00 PM", "2:00 PM", "3:00 PM",
  "4:00 PM", "5:00 PM",
];

const INIT = {
  name: "",
  email: "",
  phone: "",
  vehicle_year: "",
  vehicle_make: "",
  vehicle_model: "",
  service_type: ALL_PACKAGES.find((p) => p.featured)?.name || ALL_PACKAGES[0].name,
  preferred_date: "",
  preferred_time: "",
  service_address: "",
  notes: "",
};

export default function QuoteForm() {
  const [form, setForm] = useState(INIT);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone || !form.service_type) {
      toast.error("Please fill in name, email, phone, and service.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/quote-requests`, form);
      setSuccess(true);
      toast.success("Thanks, your quote request has been received. We'll contact you shortly.");
      setForm(INIT);
    } catch (err) {
      console.error(err);
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Something went wrong. Please try again or call us.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="quote" data-testid="quote-section" className="section bg-[#0a0a0a] border-y border-white/5">
      <div className="container-pd">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <div className="flex items-center gap-3 mb-5">
              <div className="divider-gold" />
              <span className="eyebrow">08 · Get Your Quote</span>
            </div>
            <h2 className="font-display font-black text-white text-3xl sm:text-4xl lg:text-5xl tracking-tighter leading-[1.05]">
              Tell us about your <span className="text-[#D4AF37]">car.</span>
            </h2>
            <p className="mt-5 text-zinc-400 leading-relaxed">
              Free, no-obligation quote. We typically respond within a few hours during business hours.
            </p>

            <div className="mt-10 space-y-5">
              <InfoRow icon={<Phone size={18} weight="bold" />} label="Call us" value="0423 004 480" href="tel:+61423004480" testid="info-phone" />
              <InfoRow icon={<EnvelopeSimple size={18} weight="bold" />} label="Email" value="admin@velocitymaintenance.net" href="mailto:admin@velocitymaintenance.net" testid="info-email" />
              <InfoRow icon={<Clock size={18} weight="bold" />} label="Location" value="Piara Waters, WA" testid="info-location" />
              <InfoRow icon={<Clock size={18} weight="bold" />} label="Hours" value="Mon–Sat · 7:30am – 7:00pm" testid="info-hours" />
            </div>
          </div>

          <div className="lg:col-span-7">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="bg-[#0c0c0c] border border-white/5 p-7 md:p-10"
            >
              {success ? (
                <SuccessState onReset={() => setSuccess(false)} />
              ) : (
                <form onSubmit={onSubmit} data-testid="quote-form" className="space-y-5">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <Field label="Your Name *">
                      <input data-testid="quote-name" required className="pd-input" value={form.name} onChange={update("name")} placeholder="Alex Morgan" />
                    </Field>
                    <Field label="Phone *">
                      <input data-testid="quote-phone" required type="tel" className="pd-input" value={form.phone} onChange={update("phone")} placeholder="(512) 555-0123" />
                    </Field>
                  </div>

                  <Field label="Email *">
                    <input data-testid="quote-email" required type="email" className="pd-input" value={form.email} onChange={update("email")} placeholder="you@email.com" />
                  </Field>

                  <div className="grid grid-cols-2 gap-5">
                    <Field label="Vehicle Make">
                      <input data-testid="quote-make" className="pd-input" value={form.vehicle_make} onChange={update("vehicle_make")} placeholder="Tesla" />
                    </Field>
                    <Field label="Vehicle Model">
                      <input data-testid="quote-model" className="pd-input" value={form.vehicle_model} onChange={update("vehicle_model")} placeholder="Model Y" />
                    </Field>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <Field label="Service *">
                      <select data-testid="quote-service" required className="pd-input" value={form.service_type} onChange={update("service_type")}>
                        {ALL_PACKAGES.map((s) => (
                          <option key={s.slug} value={s.name}>{s.category} — {s.name} (from ${s.price})</option>
                        ))}
                        <option value="Not sure yet">Not sure yet — recommend something</option>
                      </select>
                    </Field>
                    <Field label="Suburb *">
                      <input data-testid="quote-address" required className="pd-input" value={form.service_address} onChange={update("service_address")} placeholder="Piara Waters" />
                    </Field>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <Field label="Preferred Date">
                      <Popover>
                        <PopoverTrigger asChild>
                          <button
                            type="button"
                            data-testid="quote-date-trigger"
                            className="pd-input flex items-center justify-between w-full text-left"
                          >
                            <span className={form.preferred_date ? "text-white" : "text-zinc-500"}>
                              {form.preferred_date
                                ? format(new Date(form.preferred_date), "EEE, d MMM yyyy")
                                : "Select date"}
                            </span>
                            <CalendarBlank size={16} weight="bold" className="text-[#D4AF37]" />
                          </button>
                        </PopoverTrigger>
                        <PopoverContent
                          align="start"
                          className="w-auto p-0 bg-[#0c0c0c] border border-white/10 text-white"
                        >
                          <Calendar
                            mode="single"
                            selected={form.preferred_date ? new Date(form.preferred_date) : undefined}
                            onSelect={(d) =>
                              setForm((f) => ({ ...f, preferred_date: d ? format(d, "yyyy-MM-dd") : "" }))
                            }
                            disabled={(d) => d < new Date(new Date().setHours(0, 0, 0, 0))}
                            initialFocus
                          />
                        </PopoverContent>
                      </Popover>
                    </Field>

                    <Field label="Preferred Time">
                      <select
                        data-testid="quote-time"
                        className="pd-input"
                        value={form.preferred_time}
                        onChange={update("preferred_time")}
                      >
                        <option value="">Select time</option>
                        {TIME_SLOTS.map((t) => (
                          <option key={t} value={t}>{t}</option>
                        ))}
                      </select>
                    </Field>
                  </div>

                  <Field label="Message">
                    <textarea data-testid="quote-notes" className="pd-input" value={form.notes} onChange={update("notes")} placeholder="Any specific concerns — pet hair, water spots, paint chips, etc." />
                  </Field>

                  <div className="pt-3 flex flex-col sm:flex-row sm:items-center gap-4">
                    <button type="submit" disabled={submitting} data-testid="quote-submit" className="btn-primary">
                      {submitting ? "Sending..." : (<>Request My Quote <ArrowRight size={16} weight="bold" /></>)}
                    </button>
                    <p className="text-xs text-zinc-500">No spam, ever. We&apos;ll only contact you about your quote.</p>
                  </div>
                </form>
              )}
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="pd-label">{label}</span>
      {children}
    </label>
  );
}

function InfoRow({ icon, label, value, href, testid }) {
  const Inner = (
    <div className="flex items-center gap-4 group" data-testid={testid}>
      <div className="w-11 h-11 flex items-center justify-center bg-[#0c0c0c] border border-white/5 text-[#D4AF37] group-hover:border-[#D4AF37]/40 transition-colors">{icon}</div>
      <div>
        <div className="text-[11px] uppercase tracking-[0.2em] text-zinc-500 font-semibold">{label}</div>
        <div className="text-white text-base mt-0.5">{value}</div>
      </div>
    </div>
  );
  return href ? <a href={href}>{Inner}</a> : Inner;
}

function SuccessState({ onReset }) {
  return (
    <div data-testid="quote-success" className="py-10 text-center">
      <div className="w-16 h-16 mx-auto bg-[#D4AF37]/10 border border-[#D4AF37]/30 flex items-center justify-center">
        <CheckCircle size={36} weight="duotone" className="text-[#D4AF37]" />
      </div>
      <h3 className="font-display font-bold text-2xl text-white mt-6">Quote request received.</h3>
      <p className="text-zinc-400 mt-3 max-w-md mx-auto leading-relaxed">
        Thanks — we got your details and will reply with a personalized quote within 24 hours (usually faster).
      </p>
      <button type="button" onClick={onReset} data-testid="quote-reset-btn" className="btn-secondary mt-8">
        Submit Another
      </button>
    </div>
  );
}
